import React, { useState } from 'react';

import { Button } from 'components/v2/Buttons/Button/Button';
import { SelectField } from 'components/v2/Form/SelectField/SelectField';
import { Heading } from 'components/v2/Typography/Heading';
import { Text } from 'components/v2/Typography/Text';
import theme from 'kb-shared/theme';
import KBContacts from 'kb-shared/utilities/kindbody_contacts';

import { DATE_RANGE_OPTIONS, DEFAULT_DATE_RANGE } from './MedicalRecords.constants';
import { useMedicalRecordsExport } from './MedicalRecords.hook';
import { Container, DateRangeField, Filters } from './MedicalRecords.styled';
import { DateRange } from './MedicalRecords.types';

export const MedicalRecords = () => {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const { generate, reset, generating, failed, downloadUrl } = useMedicalRecordsExport();
  const selectedOption = DATE_RANGE_OPTIONS.find(option => option.value === dateRange);

  const onDateRangeChange = (value: string) => {
    setDateRange(value as DateRange);
    reset();
  };

  return (
    <Container>
      <Heading tag="div" styledAs="h4" noMargin>
        Medical Records
      </Heading>
      <Text tag="p" size="md">
        Generate a PDF with your Kindbody medical records, including labs, cycle plans, notes,
        ultrasounds, prescriptions and embryology reports.
      </Text>

      <Filters>
        <DateRangeField>
          <SelectField
            id="medical-records-date-range"
            label="Date Range"
            options={DATE_RANGE_OPTIONS}
            selected={selectedOption ? [selectedOption] : []}
            onChange={option => onDateRangeChange(option.value)}
            hideHelperText
          />
        </DateRangeField>
        <Button
          label={generating ? 'Generating PDF...' : 'Generate PDF'}
          category="primary"
          size="lg"
          isDisabled={generating}
          onClick={() => generate(dateRange)}
        />
        {downloadUrl ? (
          <Button
            label="Download PDF"
            category="secondary"
            size="lg"
            onClick={() => window.open(downloadUrl, '_blank')}
          />
        ) : null}
      </Filters>

      {/* Always mounted, so screen readers announce when the PDF is being generated and when it's ready */}
      <div role="status">
        {generating ? (
          <Text tag="p" size="sm">
            Your PDF is being generated. This can take a few minutes, please keep this page open.
          </Text>
        ) : null}
        {downloadUrl ? (
          <Text tag="p" size="sm">
            Your PDF is ready to download.
          </Text>
        ) : null}
      </div>
      {failed ? (
        <div role="alert">
          <Text tag="p" size="sm" color={theme.colors.semantic.red}>
            {`We couldn't generate your PDF. Please try again or contact us at ${KBContacts.navigatorEmail}.`}
          </Text>
        </div>
      ) : null}
    </Container>
  );
};
